import React, { ChangeEvent, useEffect, useState } from "react";
import { Table, TableHeader, TableContent, Checkbox } from "@forge/common";

import "./list-table-view.scss";
import { textResponsive } from "../../common/utils/helper";
import { useAuth } from "../../../../../common/hooks/useAuth";
import { REQUIRED_PERMISSIONS } from "../../../../../common/authentication/config";
interface TableHeaderData {
  id: string;
  content: string | React.ReactNode;
  className: string;
  canResize?: boolean;
}
interface CourseData {
  CourseName: string;
  CourseDescription: string;
  courseid: string;
  IsDraft: string;
  LastModifiedDate: string;
  LastModifiedBy: string;
  Duration: string;
  vAppTemplateID: string;
  vAppTemplateName: string;
  vmDetails: any;
}

interface TableListViewProps {
  courseData: CourseData[];
  selectedItems: string[];
  selectedCard?: number;
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  onSelectAllRef?: React.MutableRefObject<(() => void) | null>;
}

const TableListView = (props: TableListViewProps) => {
  const { hasPermission } = useAuth();
  const handleSelectAll = (e: any) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      const allIds: any = [];
      props.courseData.forEach((item) => {
        if (item.courseid !== "disabled-row" && item.courseid !== "no-data") {
          allIds.push(item.courseid);
        }
      });
      props.setSelectedItems(allIds);
    } else {
      props.setSelectedItems([]);
    }
  };

  useEffect(() => {
    if (props.onSelectAllRef) {
      props.onSelectAllRef.current = () => {
        const selectableItemsCount = props.courseData.filter(
          (item) =>
            item.courseid !== "disabled-row" && item.courseid !== "no-data"
        ).length;
        const allSelected =
          props.selectedItems?.length === selectableItemsCount &&
          selectableItemsCount > 0;

        if (allSelected) {
          props.setSelectedItems([]);
        } else {
          const allIds: any = [];
          props.courseData.forEach((item) => {
            if (
              item.courseid !== "disabled-row" &&
              item.courseid !== "no-data"
            ) {
              allIds.push(item.courseid);
            }
          });
          props.setSelectedItems(allIds);
        }
      };
    }
  }, [props.courseData, props.selectedItems, props.setSelectedItems]);

  const onItemSelect = (data: string, isChecked: boolean) => {
    if (isChecked) {
      if (!props.selectedItems.includes(data)) {
        props.setSelectedItems([...props.selectedItems, data]);
      }
    } else {
      props.setSelectedItems(
        props.selectedItems.filter((item) => item !== data)
      );
    }
  };

  const initialTableHeaderData: TableHeaderData[] = [
    {
      id: "checkbox",
      content: (
        <div className="flex items-center">
          <div
            className={`text-[#A0A0A0] ${textResponsive(
              "text-sm"
            )} font-medium flex`}
          >
            <Checkbox
              id="selectAll"
              className="table-checkbox bg-[#202020]"
              checked={false}
              onChange={handleSelectAll}
            />
          </div>
        </div>
      ),
      className: "",
    },
    {
      id: "Course Name",
      content: (
        <div className="flex items-center">
          <div
            className={`text-[#A0A0A0] ${textResponsive(
              "text-sm"
            )} font-medium`}
          >
            COURSE NAME
          </div>
        </div>
      ),
      className: "",
    },
    {
      id: "Course Description",
      content: (
        <div className="flex items-center">
          <div
            className={`text-[#A0A0A0] ${textResponsive(
              "text-sm"
            )} font-medium`}
          >
            DESCRIPTION
          </div>
        </div>
      ),
      className: "",
    },
    {
      id: "State",
      content: (
        <div className="flex items-center">
          <div
            className={`text-[#A0A0A0] ${textResponsive(
              "text-sm"
            )} font-medium`}
          >
            {props.selectedCard === 3 ? "STATUS" : "STATE"}
          </div>
        </div>
      ),
      className: "w-[150px]",
    },
    {
      id: props.selectedCard === 3 ? "VM Template Associated" : "vAppTemplateName",
      content: (
        <div className="flex items-center">
          <div
            className={`text-[#A0A0A0] ${textResponsive(
              "text-sm"
            )} font-medium`}
          > {props.selectedCard === 3 ? "VM TEMPLATE ASSOCIATED" : "VAPP TEMPLATE NAME"}
           
          </div>
        </div>
      ),
      className: "",
    },
    {
      id: "user-data-time",
      content: (
        <div className="flex items-center">
          <div
            className={`text-[#A0A0A0] ${textResponsive(
              "text-sm"
            )} font-medium`}
          >
            USER, DATE & TIME
          </div>
        </div>
      ),
      className: "",
    },
    {
      id: "actions",
      content: (
        <div className="flex items-center">
          <div
            className={`text-[#A0A0A0] ${textResponsive(
              "text-sm"
            )} font-medium`}
          >
            ACTIONS
          </div>
        </div>
      ),
      className: "",
    },
  ];

  const [tableContentData, setTableContentData] = useState<any>(
    props.courseData
  );
  const [tableHeaderData, setTableHeaderData] = useState(
    initialTableHeaderData
  );

  useEffect(() => {
    const updatedTableContentData = props?.courseData?.map((item: any) => {
      const isSelected = props.selectedItems.includes(item?.courseid);
      const updatedCells = item.cell.map((cell: any, index: any) => {
        if (index === 0) {
          return {
            ...cell,
            content: (
              <div className="flex">
                <Checkbox
                  id={item?.courseid}
                  className={`table-checkbox bg-[#202020] ${
                    item?.courseid === "disabled-row" || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE)
                      ? "opacity-30 cursor-disable"
                      : "cursor-pointer"
                  }`}
                  checked={Boolean(isSelected)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (item?.courseid === "disabled-row" || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE)) {
                      e.preventDefault();
                      return;
                    }
                    onItemSelect(item?.courseid, e.target.checked);
                  }}
                  data-testid={`course-row-checkbox-${item?.courseid}`}
                />
              </div>
            ),
          };
        }
        return cell;
      });

      return {
        ...item,
        cell: updatedCells,
      };
    });
    setTableContentData(updatedTableContentData);
  }, [props.courseData]);

  useEffect(() => {
    if (tableContentData.length) {
      const updatedTableContentData = tableContentData.map((item: any) => {
        const isSelected = props.selectedItems.includes(item?.courseid);
        const updatedCells = item.cell.map((cell: any, index: any) => {
          if (index === 0) {
            return {
              ...cell,
              content: (
                <div className="flex">
                  <Checkbox
                    id={item?.courseid}
                    className={`table-checkbox bg-[#202020] ${
                      item?.courseid === "disabled-row" || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE)
                        ? "opacity-30 cursor-disable"
                        : "cursor-pointer"
                    }`}
                    checked={Boolean(isSelected)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (item?.courseid === "disabled-row" || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE)) {
                        e.preventDefault();
                        return;
                      }
                      onItemSelect(item?.courseid, e.target.checked);
                    }}
                    data-testid={`course-row-checkbox-${item?.courseid}`}
                  />
                </div>
              ),
            };
          }
          return cell;
        });

        return {
          ...item,
          cell: updatedCells,
        };
      });
      setTableContentData(updatedTableContentData);
    }
    const updatedTableHeaderData = tableHeaderData.map((header) => {
      if (header.id === "checkbox") {
        const selectableItemsCount = props.courseData.filter(
          (item) =>
            item?.courseid !== "disabled-row" && item?.courseid !== "no-data"
        ).length;
        let allSelected =
          props.selectedItems?.length === selectableItemsCount &&
          selectableItemsCount > 0;
        return {
          ...header,
          content: (
            <div className="flex items-center">
              <div
                className={`text-[#A0A0A0] ${textResponsive(
                  "text-sm"
                )} font-medium flex`}
              >
                <Checkbox
                  id="selectAll"
                  className={`table-checkbox bg-[#202020] ${
                    selectableItemsCount === 0 || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE)
                      ? "opacity-30 cursor-disable"
                      : "cursor-pointer"
                  }`}
                  checked={
                    selectableItemsCount === 0 ? false : Boolean(allSelected)
                  }
                  onChange={() => {
                    if (selectableItemsCount === 0 || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE)) {
                      return;
                    }
                    handleSelectAll({ target: { checked: !allSelected } });
                  }}
                />
              </div>
            </div>
          ),
        };
      }
      if (header.id === "VMS Linked" || header.id === "courseTemplateAssociated") {
        return {
          ...header,
          id: props.selectedCard === 3 ? "courseTemplateAssociated" : "VMS Linked",
          content: (
            <div className="flex items-center">
              <div
                className={`text-[#A0A0A0] ${textResponsive(
                  "text-sm"
                )} font-medium`}
              >
                {props.selectedCard === 3 ? "VM TEMPLATE ASSOCIATED" : "VAPP TEMPLATE NAME"}
              </div>
            </div>
          ),
        };
      }
      return header;
    });
    setTableHeaderData(updatedTableHeaderData);
  }, [props.selectedItems, props.selectedCard]);

  useEffect(() => {
    const updatedTableHeaderData = tableHeaderData.map((header) => {
      if (header.id === "checkbox") {
        const selectableItemsCount = props.courseData.filter(
          (item) =>
            item.courseid !== "disabled-row" && item.courseid !== "no-data"
        ).length;
        let allSelected =
          props.selectedItems?.length === selectableItemsCount &&
          selectableItemsCount > 0;
        return {
          ...header,
          content: (
            <div className="flex items-center">
              <div
                className={`text-[#A0A0A0] ${textResponsive(
                  "text-sm"
                )} font-medium flex`}
              >
                <Checkbox
                  id="selectAll"
                  className={`table-checkbox bg-[#202020] ${
                    selectableItemsCount === 0 || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE)
                      ? "opacity-30 cursor-disable"
                      : "cursor-pointer"
                  }`}
                  checked={
                    selectableItemsCount === 0 ? false : Boolean(allSelected)
                  }
                  onChange={() => {
                    if (selectableItemsCount === 0 || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE) || !hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE)) {
                      return;
                    }
                    handleSelectAll({ target: { checked: !allSelected } });
                  }}
                />
              </div>
            </div>
          ),
        };
      }
      return header;
    });
    setTableHeaderData(updatedTableHeaderData);
  }, [props.courseData]);

  return (
    <div className="table-list-view px-4">
      <Table>
        <TableHeader data={tableHeaderData} />
        <TableContent data={tableContentData} variant="tall" />
      </Table>
    </div>
  );
};
export default TableListView;
